/**
 * Zona 3: **lo que está sano, en una línea y colapsado.**
 *
 * Una cuenta que consume estable y con saldo holgado no pide nada hoy, y ocupar
 * media pantalla con filas verdes es exactamente lo que hace que no se vea lo que
 * está roto arriba. Mismo patrón que el Overview: un `<details>` nativo, que se
 * abre con teclado y que el lector de pantalla anuncia solo.
 *
 * Lo que la línea cerrada tiene que informar es **el conteo y qué significa estar
 * sano acá**, porque no es una opinión: es no estar en caída ni por agotar
 * créditos, con el umbral dicho. Sin eso, "6 cuentas sin sobresaltos" sería un
 * elogio sin definición.
 *
 * Los nombres no se listan: el payload manda `clientIds`, no nombres, y traducir
 * ids a nombres desde otra tabla de la pantalla es justo el tipo de cruce que
 * puede quedar desincronizado. El detalle abierto ofrece la lista completa de
 * cuentas donde sí viven los nombres.
 */
import type { UsageSanas } from '@ops/contracts'
import { formatMetricValue } from '@ops/contracts'

import { plural } from '../../lib/format'
import { Card, HealthDot, Icon } from '../../ui'

export interface CuentasSanasProps {
  sanas: UsageSanas
  umbralCaidaPct: number
  umbralAgotamientoMeses: number
  /** Cuentas del período sin ninguna medición: no están sanas, están sin leer. */
  sinMedicion: number
}

export function CuentasSanas({
  sanas,
  umbralCaidaPct,
  umbralAgotamientoMeses,
  sinMedicion,
}: CuentasSanasProps) {
  if (sanas.cantidad === 0) {
    return (
      <Card padding="sm" hundida>
        <p className="inline-flex items-center gap-2 text-meta text-muted-strong">
          <Icon name="sin-dato" size={13} />
          Ninguna cuenta está sana todavía: no hay nada para colapsar acá.
        </p>
      </Card>
    )
  }

  return (
    <Card padding="none" hundida>
      <details data-testid="usage-zona-sana">
        <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-4 py-3 text-meta text-text marker:content-none">
          <HealthDot health="ok" size="sm" showLabel={false} />
          {/* Conteo y definición en el mismo nodo de texto: partidos en dos, el
              resumen se lee "6cuentas" para un lector de pantalla. */}
          <span className="text-muted-strong">
            <b className="tabular font-semibold text-text">
              {formatMetricValue(sanas.cantidad, 'count')}
            </b>{' '}
            {plural(
              sanas.cantidad,
              'cuenta consumiendo sin sobresaltos',
              'cuentas consumiendo sin sobresaltos',
            )}
          </span>
          <Icon
            name="chevron"
            size={12}
            className="ml-auto text-muted-strong"
            title="Ver qué significa una cuenta sana acá"
          />
        </summary>

        <div className="border-t border-border p-4">
          <p className="max-w-prosa text-meta leading-relaxed text-muted-strong">
            Son las cuentas <b className="font-semibold text-text">con consumo medido</b> que
            no cayeron{' '}
            <b className="font-semibold text-text">
              {formatMetricValue(umbralCaidaPct, 'percent')}
            </b>{' '}
            o más contra el mes anterior y que tienen más de{' '}
            <b className="font-semibold text-text">
              {formatMetricValue(umbralAgotamientoMeses, 'months')}
            </b>{' '}
            de autonomía de créditos. Nada más: estar sana acá no dice que la
            cuenta esté sana en Clientes ni en Revenue, que miden otra cosa.
          </p>
          {sinMedicion > 0 ? (
            <p
              data-testid="usage-sanas-sin-medicion"
              className="mt-2 inline-flex max-w-prosa items-start gap-2 text-meta leading-relaxed text-muted-strong"
            >
              <Icon
                name="sin-dato"
                size={13}
                className="mt-0.5 shrink-0 text-muted-strong"
              />
              <span>
                Aparte hay{' '}
                <b className="font-semibold text-text">
                  {formatMetricValue(sinMedicion, 'count')}
                </b>{' '}
                {plural(
                  sinMedicion,
                  'cuenta sin ninguna medición en el período',
                  'cuentas sin ninguna medición en el período',
                )}
                . No están acá: no se sabe si están sanas, y contarlas como sanas
                sería afirmar algo que nadie midió.
              </span>
            </p>
          ) : null}
        </div>
      </details>
    </Card>
  )
}
